import {
  type CSSProperties,
  type TransitionEvent,
  useCallback,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { Button } from '../buttons/Button';
import { GoogleFontIcon } from '../icons/GoogleFontIcon';
import { SVGIcon } from '../icons/SVGIcon';

import { clampSourcesPanelWidth } from './assistantPanelWidth';
import { formatSourcesPanelTitle } from './formatSourcesPanelTitle';
import {
  groupAssistantSources,
  sourceForCitationNumber,
} from './groupAssistantSources';
import { openAssistantSource } from './openAssistantSource';
import { SourceCard } from './SourceCard';
import {
  type AiAssistantPreviewDocument,
  type AiAssistantSource,
  type AiAssistantSourcesData,
} from './types';
import { useOpenedSource } from './useOpenedSource';
import { usePanelResize } from './usePanelResize';
import { useRetainedPanelContent } from './useRetainedPanelContent';
import { useTargetSourceScrollIntoView } from './useTargetSourceScrollIntoView';

import styles from './SourcesPanel.module.scss';

export type SourcesPanelProps = {
  data: AiAssistantSourcesData | null;
  targetSource?: number | null;
  rightOffset: number;
  width: number;
  /** The file currently open in the document preview (null when closed). */
  openDocumentFile?: string | null;
  onClose: () => void;
  onCloseDocument?: () => void;
  /** Requests a backend-created archive containing all document sources. */
  onDownloadAll?: () => void;
  /** Download one cited document. Live sources have `documentId`, not `file`. */
  onDownloadSource?: (source: AiAssistantSource) => void;
  /** Called when a page/link source is opened; the consumer owns routing. */
  onNavigate?: (to: string) => void;
  onOpenDocument?: (document: AiAssistantPreviewDocument) => void;
  onWidthChange?: (width: number) => void;
};

export function SourcesPanel({
  data,
  targetSource,
  rightOffset,
  width,
  openDocumentFile = null,
  onClose,
  onCloseDocument,
  onDownloadAll,
  onDownloadSource,
  onNavigate,
  onOpenDocument,
  onWidthChange,
}: SourcesPanelProps) {
  const { t } = useTranslation();
  const { active, handleTransitionEnd, isOpen } = useRetainedPanelContent(data);
  const listRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  const { isResizing, startResize } = usePanelResize({
    getWidth: (clientX) => globalThis.window.innerWidth - rightOffset - clientX,
    onResize: (nextWidth) => onWidthChange?.(clampSourcesPanelWidth(nextWidth)),
  });

  const { markOpenedSource, openedSource } = useOpenedSource({
    messageId: active?.messageId,
    openDocumentFile,
    sources: active?.sources,
    targetSource,
  });

  const flashedCard = useTargetSourceScrollIntoView({
    cardRefs,
    isOpen,
    listRef,
    messageId: active?.messageId,
    targetSource,
  });

  const handlePanelTransitionEnd = useCallback(
    (event: TransitionEvent<HTMLElement>) => {
      const slideOutFinished = handleTransitionEnd(event);
      if (slideOutFinished) {
        cardRefs.current.clear();
      }
    },
    [handleTransitionEnd]
  );

  function handleOpenSource(source: AiAssistantSource, number: number) {
    markOpenedSource(number);
    openAssistantSource(source, {
      onCloseDocument,
      onNavigate,
      onOpenDocument,
    });
  }

  const sources = active?.sources ?? [];
  const groups = groupAssistantSources(sources);
  const documentGroups = groups.filter(
    (group) => group.sources[0]?.kind === 'document'
  );
  const linkGroups = groups.filter(
    (group) => group.sources[0]?.kind === 'page'
  );
  const orderedGroups = [...documentGroups, ...linkGroups];
  const title = formatSourcesPanelTitle(
    t,
    documentGroups.length,
    linkGroups.length
  );
  const downloadableDocuments = sources.filter(
    (source) => source.kind === 'document' && source.file
  ).length;
  const showDownloadAll =
    downloadableDocuments > 0 && typeof onDownloadAll === 'function';

  return (
    <aside
      aria-hidden={!isOpen}
      aria-label={title}
      className={clsx(styles['panel'], {
        [styles['panel--open']]: isOpen,
        [styles['panel--resizing']]: isResizing,
      })}
      data-testid="ai-assistant-sources-panel"
      onTransitionEnd={handlePanelTransitionEnd}
      role="dialog"
      style={
        { right: `${rightOffset}px`, width: `${width}px` } as CSSProperties
      }
    >
      <div
        aria-hidden="true"
        className={styles['resize-handle']}
        data-testid="ai-assistant-sources-resize-handle"
        onPointerDown={startResize}
      />

      <header className={styles['header']}>
        <span className={styles['title']}>
          <GoogleFontIcon name="menu_book" solid />
          <span className={styles['title-text']}>
            {title}
          </span>
        </span>
        <div className={styles['actions']}>
          {showDownloadAll ? (
            <Button
              icon={<GoogleFontIcon name="folder_zip" solid />}
              onClick={onDownloadAll}
              title={t('app.aiAssistant.downloadAllDocuments')}
              variant="text"
              data-testid="ai-assistant-sources-download-all"
            />
          ) : null}
          <Button
            icon={<SVGIcon name="close" />}
            onClick={() => onClose()}
            title={t('app.aiAssistant.closeSources')}
            variant="text"
            data-testid="ai-assistant-sources-close"
          />
        </div>
      </header>

      <div className={styles['list']} ref={listRef}>
        {orderedGroups.map((group) => {
          const representative = group.sources[0];

          return (
            <SourceCard
              activeNumber={openedSource}
              citations={group.numbers.map((number, index) => {
                const citationSource = group.sources[index];
                return {
                  number,
                  excerpt: citationSource?.excerpt,
                  pages:
                    citationSource?.kind === 'document'
                      ? citationSource.pages
                      : undefined,
                };
              })}
              flashedNumber={flashedCard}
              key={group.key}
              numbers={group.numbers}
              onDownload={onDownloadSource}
              onOpen={(citationNumber) =>
                handleOpenSource(
                  sourceForCitationNumber(group, citationNumber),
                  citationNumber
                )
              }
              ref={(node) => {
                for (const number of group.numbers) {
                  if (node) {
                    cardRefs.current.set(number, node);
                  } else {
                    cardRefs.current.delete(number);
                  }
                }
              }}
              source={representative}
            />
          );
        })}
      </div>
    </aside>
  );
}
